import { Injectable } from '@angular/core';
import { CallbackService, CodeChallenge } from '@app/modules/callback/callback.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { LaunchdarklyService } from '@acesso-io/launchdarkly-client-ts/lib/launchdarkly';
import { StateToken } from '@app/_shared/models/state-token.model';
import * as ld from 'launchdarkly-js-client-sdk';
import { JWTClaimOverrideName } from './jwt.interface';
import {
  clearStorage,
  getCurrentOrganization,
  getPeoplePositionId,
  getTokenStorage,
  SessionStorage
} from '@app/_shared/services/storage/storage.service';

declare let environment: any;

@Injectable()
export class AuthService {
  private identity_url: string = environment.identity_url;
  private base_url: string = environment.base_url;
  private url: string = environment.url;

  constructor(
    private http: HttpClient,
    public jwtHelper: JwtHelperService,
    public callbackService: CallbackService,
    private launchDarklyService: LaunchdarklyService
  ) {}

  loggedIn() {
    const token: string = getTokenStorage();
    return token != null && !this.jwtHelper.isTokenExpired(token);
  }

  logoutApi(): Observable<any> {
    return this.http.get<any>(`${this.url}/logout`);
  }

  logout() {
    this.logoutApi().subscribe(() => {
      const state = SessionStorage.getItem('currentPositionToken');
      clearStorage();

      window.location.href = `${this.identity_url}/signout?redirect_uri=${this.base_url}/profile/intro/${state}`;
    });
  }

  async startPKCEAuthenticationFlow(redirect_uri: string, loginPath: string, state: string): Promise<string> {
    await this.setOverrideContext();
    let identityFinalURL = this.getIdentityUrl(loginPath, state, redirect_uri, 'token');

    const codeChallenge = await this.callbackService
      .getCodeChallenge()
      .toPromise()
      .catch(err => {
        console.error(err);
        return {} as CodeChallenge;
      });

    if (codeChallenge.raw && codeChallenge.method) {
      identityFinalURL = this.getIdentityUrl(loginPath, state, redirect_uri, 'code');

      let pkceQueryParams = `&code_challenge=${codeChallenge.raw}&code_challenge_method=${codeChallenge.method}`;
      identityFinalURL = `${identityFinalURL}${pkceQueryParams}`;
    }

    return identityFinalURL;
  }

  async setOverrideContext() {
    const position = getPeoplePositionId();
    const organization = getCurrentOrganization();

    let context: ld.LDContext;

    if (organization) {
      context = {
        kind: 'multi',
        organization: {
          key: organization
        },
        user: {
          key: position,
          position,
          appName: 'form'
        }
      } as ld.LDMultiKindContext;
    } else {
      context = {
        kind: 'user',
        key: '00000000-0000-0000-0000-000000000000',
        appName: 'form'
      } as ld.LDSingleKindContext;
    }

    await this.launchDarklyService.overrideContext({ context });
  }

  getIdentityUrl(loginPath: string, state: string, redirectURI: string, responseType: string): string {
    return `${this.identity_url}/${loginPath}?scope=rh:*&state=${state}&redirect_uri=${redirectURI}&response_type=${responseType}&client_id=8aead3e6-b295-4f77-b314-86b975ee3ae2&tenant_id=7c15017b-3c9f-45e9-9833-53fcc82247a8`;
  }

  setOrganizationInLocalstorage(key: string, token: string) {
    const payload = this.jwtHelper.decodeToken(token) as StateToken;
    if (payload?.org) {
      SessionStorage.setItem(key, payload.org);
    }
  }

  setJWTClaimInLocalstorage(claims: Array<JWTClaimOverrideName>, token: string) {
    const payload = this.jwtHelper.decodeToken(token) as StateToken;
    if (payload) {
      for (let claim of claims) {
        if (payload[claim.Name]) {
          SessionStorage.setItem(claim.OverrideName, payload[claim.Name]);
        }
      }
    }
  }

  definePreSettings(token) {
    const claims = [
      { Name: 'org', OverrideName: 'originOrganizationId' },
      { Name: 'pos', OverrideName: 'originPositionId' }
    ] as Array<JWTClaimOverrideName>;

    this.setJWTClaimInLocalstorage(claims, token);
    SessionStorage.setItem('originAppName', 'form');
  }
}
